import { Decimal } from 'decimal.js';
import { ValidationError } from './errors/index.js';
function flipLedgerSide(ledgerSide) {
    switch (ledgerSide) {
        case 'credit':
            return 'debit';
        case 'debit':
            return 'credit';
        default:
            // ledgerside: never
            return ledgerSide;
    }
}
function flipLedgerSideOpt(ledgerSide) {
    if (ledgerSide === null) {
        return null;
    }
    return flipLedgerSide(ledgerSide);
}
/*
 * Accounting is driven off of the concept of journal entries, where there's no such thing as negative numbers,
 * only credits and debits. For our reports (and for sections of reports, which aggregate accounts) what we really care
 * about is the balance, i.e. the side of the ledger that has a larger total (credits or debits) and the magnitude of
 * that balance, The `LedgerBalance` class represents this concept in our reports (and, in the future, is probably
 * the right abstraction for journal entries and for ledger trial-balances as well).
 * For context, whether a LedgerBalance should be thought of as positive or negative is a property of an account:
 * for Assets, and Expense accounts, having a surplus of debits is considered positive; for Liability, Equity, and
 * Revenue accounts, having a surplus of credits is considered positive.
 */
export class LedgerBalance {
    value;
    ledgerSide;
    /**
     *
     * @param value The absolute value of the ledger balance. Always non-negative.
     * @param ledgerSide The side of the ledger that has the surplus. Null if, and only if, the value is zero.
     * @private
     */
    constructor(value, ledgerSide) {
        this.value = value;
        this.ledgerSide = ledgerSide;
        if ((ledgerSide === null) !== value.isZero()) {
            throw new ValidationError(`value ${value.toString()} must be 0 if and only if ${ledgerSide} is null`);
        }
    }
    static create(value, ledgerSide) {
        const rawDecimalValue = value instanceof Decimal ? value : new Decimal(value);
        if (rawDecimalValue.isZero()) {
            return new LedgerBalance(rawDecimalValue, null);
        }
        if (ledgerSide === null) {
            throw new ValidationError(`Nonzero ledger balance ${rawDecimalValue.toString()} must specify ledgerSide`);
        }
        if (rawDecimalValue.isNegative()) {
            return new LedgerBalance(rawDecimalValue.negated(), flipLedgerSide(ledgerSide));
        }
        return new LedgerBalance(rawDecimalValue, ledgerSide);
    }
    isZero() {
        // this.ledgerSide is null iff this.value.isZero, as enforced in the constructor
        return this.ledgerSide === null;
    }
    nonZero() {
        return this.ledgerSide !== null;
    }
    neg() {
        return LedgerBalance.create(this.value, flipLedgerSideOpt(this.ledgerSide));
    }
    plus(other) {
        if (this.value.isZero()) {
            return other;
        }
        if (other.value.isZero()) {
            return this;
        }
        // neither this nor other is 0, so ledgerSide must be defined in both cases
        if (this.ledgerSide === other.ledgerSide) {
            return LedgerBalance.create(this.value.plus(other.value), this.ledgerSide);
        }
        // using `create` handles normalizing the subtraction
        return LedgerBalance.create(this.value.minus(other.value), this.ledgerSide);
    }
    minus(other) {
        return this.plus(other.neg());
    }
    times(other) {
        return LedgerBalance.create(this.value.times(other), this.ledgerSide);
    }
    dividedBy(other) {
        return LedgerBalance.create(this.value.dividedBy(other), this.ledgerSide);
    }
    toDecimalPlaces(dp, rm) {
        return LedgerBalance.create(this.value.toDecimalPlaces(dp, rm), this.ledgerSide);
    }
    toFixed(decimalPlaces) {
        return this.value.toFixed(decimalPlaces);
    }
    equals(other) {
        return this.value.equals(other.value) && this.ledgerSide === other.ledgerSide;
    }
    toString() {
        if (this.isZero()) {
            return '0';
        }
        return `${this.value.toString()} ${this.ledgerSide}`;
    }
}
//# sourceMappingURL=LedgerBalance.js.map